import { createGetAPI, createPostAPI, RRRFetch } from '@cuemath/rest-api';
import { stringify } from 'query-string';

import { BASE_URL } from '../constants/api';

// GET /pricing/region-payout/ — returns { rows: [...] }. Optional query: class_ratio,
// validity_mode (active|all|as_of), as_of_date. Default (no validity_mode) = active today.
const { useGet: useRegionPayoutGet, invalidate: invalidateRegionPayout } = createGetAPI({
  getURL: (_, query) => `${BASE_URL}/pricing/region-payout/?${stringify(query || {})}`,
});

// POST /pricing/region-payout/ — body { region, course, grade_range, enrollment_type,
// class_ratio, fee_per_class, validity_start, validity_end?, currency?, actor_id }; creates
// a new effective-dated window. Overlapping an existing window returns a 400.
const { usePost: useRegionPayoutPost } = createPostAPI({
  getURL: () => `${BASE_URL}/pricing/region-payout/`,
});

// POST /pricing/region-payout/batch/ — creates many windows in ONE request instead of
// one POST per grid cell. Body { entries: [ { region, course, grade_range,
// enrollment_type, class_ratio, fee_per_class, validity_start, validity_end? }, ... ] }.
// Best-effort per entry: an entry that overlaps an existing window fails in isolation
// and comes back in `failures` (by its index in `entries`) so only those cells are
// flagged while the rest are created. Returns { ok, created, failures }; ok is false
// only when the whole request failed (network / malformed batch), not for per-cell
// collisions.
const postRegionPayoutBulk = async entries => {
  try {
    const { data } = await RRRFetch.fetch(`${BASE_URL}/pricing/region-payout/batch/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entries }),
    });
    return {
      ok: true,
      created: (data && data.created) || [],
      failures: (data && data.failures) || [],
    };
  } catch (e) {
    return {
      ok: false,
      error: (e && e.error && e.error.message) || 'Failed to create windows.',
    };
  }
};

// PATCH /pricing/region-payout/{id}/ — edits an existing window's validity. Body
// carries the field being changed, e.g. { validity_end }. Moving validity_end into
// a neighbouring window returns a 400.
const patchRegionPayoutEntry = async (id, body) => {
  try {
    await RRRFetch.fetch(`${BASE_URL}/pricing/region-payout/${id}/`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return { ok: true };
  } catch (e) {
    return {
      ok: false,
      error: (e && e.error && e.error.message) || 'Failed to update window.',
    };
  }
};

// PATCH /pricing/region-payout/batch/ — edits the validity of many windows in ONE
// atomic request. Body { row_ids: [...], validity_end } (or validity_start). All-or-
// nothing: if any row is frozen/missing or a new bound overlaps a neighbour, the
// server rejects the whole batch with a 400 and nothing changes. Replaces the old
// per-cell PATCH loop for the region-wide end-date edit.
const patchRegionPayoutBatch = async (rowIds, body) => {
  try {
    await RRRFetch.fetch(`${BASE_URL}/pricing/region-payout/batch/`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ row_ids: rowIds, ...body }),
    });
    return { ok: true };
  } catch (e) {
    return {
      ok: false,
      error: (e && e.error && e.error.message) || 'Failed to update windows.',
    };
  }
};

export {
  useRegionPayoutGet,
  invalidateRegionPayout,
  useRegionPayoutPost,
  postRegionPayoutBulk,
  patchRegionPayoutEntry,
  patchRegionPayoutBatch,
};
